import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowBigUp, ArrowBigDown, MessageCircle, Clock, Plus, TrendingUp, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'

interface Topic {
  _id: string
  title: string
  content: string
  author: string
  votes: number
  commentCount: number
  tags: string[]
  createdAt: string
  userVote: 'up' | 'down' | null
}

export default function Forum() {
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [category, setCategory] = useState('recent')
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const { t } = useTranslation()

  const { data, isLoading } = useQuery({
    queryKey: ['forum-topics', category],
    queryFn: () => api.get(`/forum/topics?category=${category}`).then(r => r.data)
  })

  const voteMutation = useMutation({
    mutationFn: ({ id, vote }: { id: string; vote: string }) =>
      api.put(`/forum/topics/${id}/vote`, { vote }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forum-topics'] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const createMutation = useMutation({
    mutationFn: () => api.post('/forum/topics', { title, content }),
    onSuccess: () => {
      setShowForm(false)
      setTitle('')
      setContent('')
      queryClient.invalidateQueries({ queryKey: ['forum-topics'] })
      toast({ title: t('forum.topicCreated') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold">{t('forum.title')}</h1>
        {isAuthenticated && (
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-1" /> {t('forum.newTopic')}
          </Button>
        )}
      </div>

      {showForm && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-6 p-4 rounded-xl bg-card border border-border">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('forum.titlePlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 mb-3" />
          <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('forum.contentPlaceholder')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[100px] mb-3" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>{t('forum.create')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>{t('forum.cancel')}</Button>
          </div>
        </motion.div>
      )}

      <div className="flex gap-2 mb-6">
        {[
          { key: 'recent', label: t('forum.recent'), icon: Clock },
          { key: 'popular', label: t('forum.popular'), icon: TrendingUp },
          { key: 'unanswered', label: t('forum.unanswered'), icon: HelpCircle },
        ].map(tab => (
          <button
            type="button"
            role="tab"
            aria-selected={category === tab.key}
            aria-label={tab.label}
            key={tab.key}
            onClick={() => setCategory(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              category === tab.key ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3 p-4 rounded-xl bg-card border border-border animate-pulse">
              <div className="flex flex-col items-center gap-1">
                <div className="w-6 h-6 bg-secondary rounded" />
                <div className="h-4 bg-secondary rounded w-4" />
                <div className="w-6 h-6 bg-secondary rounded" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-secondary rounded w-3/4" />
                <div className="h-3 bg-secondary rounded w-full" />
                <div className="h-3 bg-secondary rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && <div className="space-y-3">
        {data?.topics?.map((topic: Topic) => (
          <motion.div
            key={topic._id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/20 transition-colors"
          >
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                aria-label={t('forum.upvote')}
                onClick={() => voteMutation.mutate({ id: topic._id, vote: 'up' })}
                className={`p-1 rounded hover:bg-secondary ${topic.userVote === 'up' ? 'text-green-500' : 'text-muted-foreground'}`}
              >
                <ArrowBigUp className="w-6 h-6" />
              </button>
              <span className="text-sm font-bold">{topic.votes}</span>
              <button
                type="button"
                aria-label={t('forum.downvote')}
                onClick={() => voteMutation.mutate({ id: topic._id, vote: 'down' })}
                className={`p-1 rounded hover:bg-secondary ${topic.userVote === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}
              >
                <ArrowBigDown className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <Link to={`/forum/${topic._id}`}>
                <h3 className="font-medium hover:text-primary transition-colors">{topic.title}</h3>
              </Link>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{topic.content}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span>{topic.author}</span>
                <span>{formatDistanceToNow(topic.createdAt)}</span>
                <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {topic.commentCount}</span>
              </div>
              <div className="flex gap-1 mt-2">
                {topic.tags?.map(tag => (
                  <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">#{tag}</span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
        {data?.topics?.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">{t('forum.noTopics')}</div>
        )}
      </div>}
    </div>
  )
}
